// Pure logic for the at-anchor Forecast module. The chart carries the story
// (glance-first, ADR 0181): what this file computes is the squall-risk band,
// the direction sweep behind the glyph ribbon, and the honest copy for a panel
// with nothing to draw.
//
// `peakGust` and `directionShift` once fed an on-screen sentence ("Peak gust
// 32 kn around 02:00 · Wind backs SW → S") that restated the chart and cost
// two lines on a height-locked screen. The sentence is gone; these now feed
// what the plot cannot carry — the direction ribbon's glyphs and the
// accessible summaries a screen reader gets instead of the curve.
//
// The gust, the squall band and the unavailable copy are @neomaris/protocol's
// (neo-page-modules.ts, cloud ADR 0393, TOM-665), so a forecast module page's
// figures and this module write the same; these are their thin wrappers.

import {
  neoPageModelUnavailableMessage,
  neoPagePeakGust,
  neoPageSquallRisk,
} from '@neomaris/protocol';

export interface ForecastPoint {
  t: number;
  v: number | null;
}

export interface PeakGust {
  kn: number;
  t: number;
}

/**
 * The strongest gust in [fromMs, toMs], with its time — peak load is what
 * breaks an anchor out, not the mean. Spoken by the chart's accessible label.
 */
export function peakGust(
  points: readonly ForecastPoint[],
  fromMs: number,
  toMs: number
): PeakGust | null {
  return neoPagePeakGust(points, fromMs, toMs);
}

const POINTS = [
  'N',
  'NNE',
  'NE',
  'ENE',
  'E',
  'ESE',
  'SE',
  'SSE',
  'S',
  'SSW',
  'SW',
  'WSW',
  'W',
  'WNW',
  'NW',
  'NNW',
] as const;

/** Degrees → the 16-point compass name a sailor actually says out loud. */
export function compassPoint(deg: number): string {
  const idx = Math.round((((deg % 360) + 360) % 360) / 22.5) % 16;
  return POINTS[idx]!;
}

/** Smallest signed angle from a to b, in (-180, 180]. */
function angleDelta(a: number, b: number): number {
  return ((((b - a) % 360) + 540) % 360) - 180;
}

export interface DirectionShift {
  fromPoint: string;
  toPoint: string;
  /** Signed degrees; negative backs (anticlockwise), positive veers. */
  deg: number;
  /** 'backs' | 'veers' — the words a forecast uses. */
  verb: 'backs' | 'veers';
}

/**
 * How the wind direction moves across the window — the one element that
 * decides whether a lee shore becomes a weather shore overnight. Spoken by
 * the direction ribbon's accessible label; the ribbon's glyphs show it.
 *
 * Returns null when the shift is too small to be worth saying; "SW → SW" is
 * noise.
 */
export function directionShift(
  points: readonly ForecastPoint[],
  fromMs: number,
  toMs: number,
  minDeg = 30
): DirectionShift | null {
  const inWindow = points.filter(
    (p): p is { t: number; v: number } => p.v !== null && p.t >= fromMs && p.t <= toMs
  );
  if (inWindow.length < 2) return null;
  const start = inWindow[0]!.v;
  const end = inWindow[inWindow.length - 1]!.v;
  const deg = angleDelta(start, end);
  if (Math.abs(deg) < minDeg) return null;
  return {
    fromPoint: compassPoint(start),
    toPoint: compassPoint(end),
    deg: Math.round(deg),
    verb: deg < 0 ? 'backs' : 'veers',
  };
}

/**
 * Evenly sampled wind directions for the ribbon — one glyph per slot, spread
 * across the window so the sweep reads left-to-right under the chart's time
 * axis. Slots the model has no direction for are null (the ribbon leaves a
 * gap rather than inventing a heading).
 */
export function directionSamples(
  points: readonly ForecastPoint[],
  fromMs: number,
  toMs: number,
  slots = 9
): Array<number | null> {
  const inWindow = points.filter((p) => p.t >= fromMs && p.t <= toMs);
  if (inWindow.length === 0 || slots < 2) return [];
  const out: Array<number | null> = [];
  for (let i = 0; i < slots; i += 1) {
    const target = fromMs + (i / (slots - 1)) * (toMs - fromMs);
    let best: ForecastPoint | null = null;
    for (const p of inWindow) {
      if (best === null || Math.abs(p.t - target) < Math.abs(best.t - target)) best = p;
    }
    out.push(best !== null && best.v !== null ? best.v : null);
  }
  return out;
}

/**
 * Why a model-fed panel has nothing to draw.
 *
 * `useForecast` collapses three different situations into `kind: 'unavailable'`
 * — no position fix, a stale fix, and a failed request — and only the payload's
 * `status` tells them apart. A panel that lumps every non-ready state into
 * "fetching…" leaves a boat with a lost fix sitting on a spinner that never
 * resolves, hiding the actual reason. Both model panels resolve their copy
 * through here so neither can drift into that.
 */
export function modelUnavailableMessage(
  status: string | undefined,
  subject: 'forecast' | 'tide'
): string {
  return neoPageModelUnavailableMessage(status, subject);
}

/** The filled weather symbols the forecast draws (DESIGN_SYSTEM.md §4). */
export type SkySymbol = 'sun' | 'partly-cloudy' | 'cloud' | 'moon';

/**
 * The sky for one hour of the model, as a filled symbol (TOM-679): the sun by
 * day and the moon by night, from the model's shortwave radiation (above zero
 * while the sun is up), with a cloud over it from the model's cloud cover. By
 * day, under 30% is the sun, under 80% the sun behind a cloud, and more the
 * cloud; by night, under 50% is the moon, and more the cloud. Null when the
 * model gives too little to say, so no symbol claims a sky it does not know.
 */
export function skySymbol(cloudPct: number | null, radiationWm2: number | null): SkySymbol | null {
  if (cloudPct !== null && cloudPct >= 80) return 'cloud';
  if (radiationWm2 === null) return null;
  if (radiationWm2 > 0) {
    if (cloudPct === null || cloudPct < 30) return 'sun';
    return 'partly-cloudy';
  }
  if (cloudPct === null || cloudPct < 50) return 'moon';
  return 'cloud';
}

/**
 * Convective-available-potential-energy band for the squall chip. Below the
 * floor the chip does not render at all — a risk indicator that is always on
 * screen is one nobody reads.
 */
export function squallRisk(capeMax: number | null): 'elevated' | 'high' | null {
  return neoPageSquallRisk(capeMax);
}
